import React, { useState, useContext, useEffect, useMemo } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Input,
  Typography,
  Popconfirm,
  message,
  Form,
  Card,
  FloatButton,
  theme,
  Modal,
  InputNumber,
} from "antd";
import dayjs from "dayjs";
import {
  ClockCircleOutlined,
  PauseCircleOutlined,
  RedoOutlined,
  StopOutlined,
  AudioOutlined,
} from "@ant-design/icons";
import { UiButton, EditIcon, UiTablePageToolbar } from "../../ui";
import AddMedicineModal from "./AddMedicineModal";
import {
  dosageTiming,
  isStatMedicine,
  medicineFrequency,
  formatMedicineDaysUsed,
  formatMedicineDurationModalLabel,
} from "../../../helpers/medicineKeys";
import {
  medicationPrescribeAPI,
  updateMedicationPrescribeAPI,
} from "../../../services/patient.services";
import AddMedicineForm from "./AddMedicineForm";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import { canEdit } from "../../../permissions/capabilities";
import { useLocation } from "react-router-dom";
import { UserContext } from "../../../context/UserContext";
import AddInitialMedicineModal from "./AddMedicine/AddInitialMedicineModal";
import useQuery from "../../../hooks/useQuery";
import RecordingModal from "./AiMedications/RecordingModal";
import useIsMobile from "../../../hooks/useIsMobile";
import styles from "./AddMedicineTable.module.css";

const { Paragraph, Text } = Typography;

const AddMedicineTable = ({
  data,
  refreshPage,
  loading,
  initial = false,
  setHistoryModal,
}) => {
  const location = useLocation();
  const isActive = useQuery()?.status === "current";
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const { userDetails } = useContext(UserContext);
  const userRole = userDetails?.currRole || getCookie(Role);
  const actionsEnabled = userRole === "Doctor" && canEdit(userRole, "medicine");
  const { token } = theme.useToken();
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [initialModal, setInitialModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showAddMedicine, setShowAddMedicine] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [nameSearch, setNameSearch] = useState("");
  const [selectedFrequency, setSelectedFrequency] = useState(null);
  const [timers, setTimers] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [startRecord, setStartRecord] = useState(false);
  const [editDurationRecord, setEditDurationRecord] = useState(null);
  const [editCanExtendDuration, setEditCanExtendDuration] = useState(false);
  const [editDurationSaving, setEditDurationSaving] = useState(false);
  const [editDurationForm] = Form.useForm();
  const [initialUpdatedAtById, setInitialUpdatedAtById] = useState({});
  const primaryAccentTagStyle = {
    color: token.colorPrimary,
    borderColor: "color-mix(in srgb, var(--ant-primary-color) 26%, white)",
    backgroundColor: "color-mix(in srgb, var(--ant-primary-color) 10%, white)",
  };

  const ACTION_ICON_SIZE = 16;

  const actionIconWrapperStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    cursor: "pointer",
    verticalAlign: "middle",
  };

  const antActionIconStyle = {
    fontSize: ACTION_ICON_SIZE,
    color: "#475467",
  };

  const renderEditIcon = (onClick, title) => (
    <span
      role="button"
      tabIndex={0}
      title={title}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      style={actionIconWrapperStyle}
    >
      <EditIcon size={ACTION_ICON_SIZE} alt="Edit" />
    </span>
  );

  const renderActionIcon = (Icon, onClick, title) => (
    <span
      role="button"
      tabIndex={0}
      title={title}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      style={actionIconWrapperStyle}
    >
      <Icon style={antActionIconStyle} />
    </span>
  );

  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const getDaysProgress = (record) => {
    const noOfDays = safeNum(record?.no_of_days ?? record?.days_left_adjusted);
    const duration = safeNum(record?.duration);
    return { noOfDays, duration };
  };

  const canExtendDuration = (record) => {
    if (isStatMedicine(record)) return false;
    const { noOfDays, duration } = getDaysProgress(record);
    return duration > 0 && noOfDays === duration;
  };

  useEffect(() => {
    setInitialUpdatedAtById((prev) => {
      const next = { ...prev };
      (data || []).forEach((r) => {
        const id = r?.id;
        if (!id) return;
        if (next[id]) return;
        next[id] = r?.created_at || r?.added_at || r?.prescribed_at || r?.updated_at;
      });
      return next;
    });
  }, [data]);

  useEffect(() => {
    const timerIds = {};

    data?.forEach((record) => {
      const baseTs = initialUpdatedAtById?.[record?.id] || record?.updated_at;
      const updatedAt = dayjs(baseTs);
      const now = dayjs();
      const secondsElapsed = now.diff(updatedAt, "second");
      const secondsLeft = Math.max(60 - secondsElapsed, 0);

      if (secondsLeft > 0) {
        timerIds[record.id] = setInterval(() => {
          setTimers((prevTimers) => ({
            ...prevTimers,
            [record.id]: Math.max(prevTimers[record.id] - 1, 0),
          }));
        }, 1000);
      }
    });

    return () => {
      Object.values(timerIds).forEach(clearInterval);
    };
  }, [data, initialUpdatedAtById]);

  const filteredData = useMemo(() => {
    const query = nameSearch.trim().toLowerCase();
    if (!query) return data || [];
    return (data || []).filter(
      (record) =>
        record?.name?.toString().toLowerCase().includes(query) ||
        record?.medicine_name?.toString().toLowerCase().includes(query)
    );
  }, [data, nameSearch]);

  const actionMedicine = (id, value) => {
    if (typeof updateMedicationPrescribeAPI === "function") {
      updateMedicationPrescribeAPI(id, value)
        .then((res) => {
          message.success(res?.data?.message);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    }
  };

  const openEditDurationModal = (record) => {
    setEditDurationRecord(record);
    setEditCanExtendDuration(canExtendDuration(record));
    editDurationForm.setFieldsValue({
      extended_duration: undefined,
      comments: record?.comments ?? "",
    });
  };

  const closeEditDurationModal = () => {
    setEditDurationRecord(null);
    setEditCanExtendDuration(false);
    editDurationForm.resetFields();
  };

  const submitEditDuration = () => {
    editDurationForm.validateFields().then((values) => {
      const record = editDurationRecord;
      if (!record?.id) return;

      const rawExtend = values?.extended_duration;
      const hasExtendValue =
        editCanExtendDuration &&
        rawExtend !== undefined &&
        rawExtend !== null &&
        rawExtend !== "" &&
        safeNum(rawExtend) > 0;

      const nextComments = (values?.comments ?? "").trim();
      const prevComments = (record?.comments ?? "").trim();

      if (!hasExtendValue && nextComments === prevComments) {
        message.info("No changes to save");
        return;
      }

      const payload = {};
      if (hasExtendValue) {
        const extendBy = safeNum(rawExtend);
        const currentTotal = safeNum(
          record?.days_left_initial ?? record?.duration
        );
        payload.extended_duration = extendBy;
        payload.days_left_initial = currentTotal + extendBy;
      }
      if (nextComments !== prevComments) {
        payload.comments = nextComments;
      }

      setEditDurationSaving(true);
      updateMedicationPrescribeAPI(record.id, payload)
        .then((res) => {
          message.success(res?.data?.message);
          closeEditDurationModal();
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message))
        .finally(() => setEditDurationSaving(false));
    });
  };

  const onFinish = (values) => {
    const times = values?.schedule?.times.map((time) =>
      dayjs(time)?.format("HH")
    );
    const {
      schedule,
      duration = null,
      days_left_initial,
      ...restValues
    } = values;

    const formData = {
      facility: Number(getCookie(FacilityID)) || userDetails?.currFacility,
      ip_admission: Number(params?.patient),
      medicine: selectedId,
      name: values?.name,
      is_initial: initial,
      duration,
      days_left_initial: days_left_initial ?? duration,
      schedule: {
        times,
      },
      ...restValues,
    };

    medicationPrescribeAPI(formData)
      .then((res) => {
        message.success(res?.data?.message);
        refreshPage();
        setShowAddMedicine(false);
        form.resetFields();
        setSelectedId(null);
        setSelectedFrequency(null);
      })
      .catch((err) => message.error(err?.response?.data?.message));
  };
  const formatTime = (time) => {
    const parsedTime = dayjs()
      .set("hour", parseInt(time, 10))
      .set("minute", 0)
      .set("second", 0);

    return parsedTime.format("hh A");
  };
  const columns = [
    {
      title: "Name",
      render: (record) => (
        <>
          <Typography>{record?.name || record?.medicine_name}</Typography>
          <Space wrap size={[8, 4]}>
            {record?.drug && (
              <Typography style={{ fontSize: "10px" }}>
                Drug : {record?.drug}
              </Typography>
            )}
            {record?.dose && (
              <Typography style={{ fontSize: "10px" }}>
                Dose: {record?.dose}
              </Typography>
            )}
          </Space>
        </>
      ),
      width: isMobile ? 180 : 250,
      fixed: "left",
    },
    // {
    //   title: "Drug",
    //   dataIndex: "Drug",
    //   key: "Drug",
    //   width: "250px",
    //   render: (value, record) =>
    //     value ? <Typography>{value}</Typography> : "-",
    // },
    // {
    //   title: "Dose",
    //   dataIndex: "dose",
    //   key: "dose",
    //   width: "100px",
    //   render: (value, record) =>
    //     value ? (
    //       <Typography>{`${value} ${
    //         record?.unit ? record?.unit : " "
    //       }`}</Typography>
    //     ) : (
    //       "-"
    //     ),
    // },
    {
      title: "Form",
      dataIndex: "form",
      key: "form",
      width: "150px",
      render: (value, record) =>
        value ? (
          <>
            {value === "Others" && record.other_form
              ? `${value} - ${record.other_form}`
              : value}
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Frequency",
      dataIndex: "frequency",
      key: "frequency",
      width: "200px",
      render: (value, record) =>
        value ? (
          <>
            <Typography>
              {medicineFrequency?.find((med) => med?.value === value)?.label ||
                "-"}
            </Typography>
            {record?.schedule?.times?.map((time, index) => (
              <Tag
                key={`${record?.id}-time-${index}`}
                style={{ marginBottom: 8, ...primaryAccentTagStyle }}
              >
                <Space>
                  <ClockCircleOutlined />
                  {time && formatTime(time)}
                </Space>
              </Tag>
            ))}
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Timing",
      dataIndex: "dosage_timing",
      key: "dosage_timing",
      width: "150px",
      render: (value, record) =>
        value ? (
          <>
            <Tag
              color={
                value === "after_food"
                  ? "orange"
                  : value === "before_food"
                  ? "magenta"
                  : "green"
              }
            >
              {dosageTiming?.find((med) => med?.value === value)?.title || "-"}
            </Tag>
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Route",
      dataIndex: "route",
      key: "route",
      width: "120px",
      render: (value, record) =>
        value ? <>{value && <Tag>{value}</Tag>}</> : "-",
    },
    {
      title: initial ? "Days Left" : "Duration",
      dataIndex: "duration",
      key: "duration",
      width: "120px",
      render: (value, record) => {
        if (isStatMedicine(record)) {
          return <Tag style={primaryAccentTagStyle}>Stat</Tag>;
        }
        const noOfDays = safeNum(record?.no_of_days ?? record?.days_left_adjusted);
        const duration = safeNum(record?.duration);
        return (
          <Tag style={primaryAccentTagStyle}>
            {formatMedicineDaysUsed(noOfDays, duration)}
          </Tag>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "comments",
      key: "comments",
      width: "200px",
      render: (value) =>
        value ? (
          <Paragraph
            className="medicine-notes-ellipsis"
            ellipsis={{
              rows: 2,
              expandable: true,
              symbol: "more",
            }}
            style={{
              width: "100%",
              maxWidth: "100%",
              marginBottom: 0,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {value}
          </Paragraph>
        ) : (
          "-"
        ),
    },
    // location?.pathname?.includes[("doctor", "nurse")] &&
  ];

  const editDaysProgress = getDaysProgress(editDurationRecord || {});

  if (actionsEnabled && isActive) {
    columns.push({
      title: "Action",
      width: isMobile ? 120 : 200,
      fixed: isMobile ? undefined : "right",
      render: (record) => {
        const now = dayjs();
        const baseTs = initialUpdatedAtById?.[record?.id] || record?.updated_at;
        const updatedAt = dayjs(baseTs);
        const secondsElapsed = now.diff(updatedAt, "second");
        const secondsLeft = Math.max(60 - secondsElapsed, 0);
        const showEditButton = secondsLeft > 0;
        const allowDurationEdit = canExtendDuration(record);

        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 6,
              maxWidth: "100%",
            }}
          >
            {record?.status === "Started" ? (
              <Space align="center" size={4}>
                {showEditButton ? (
                  <>
                    {renderEditIcon(() => {
                      setAddModal(true);
                      setEditData(record);
                    })}
                    <Text type="secondary">{`${secondsLeft}s`}</Text>
                  </>
                ) : (
                  renderEditIcon(
                    () => openEditDurationModal(record),
                    allowDurationEdit
                      ? "Edit duration & notes"
                      : "Edit notes"
                  )
                )}

                {!initial &&
                  renderActionIcon(
                    PauseCircleOutlined,
                    () =>
                      actionMedicine(record?.id, { status: "Withheld" }),
                    "Pause"
                  )}

                <Popconfirm
                  title="Sure to stop"
                  onConfirm={() =>
                    actionMedicine(record?.id, { status: "Stopped" })
                  }
                >
                  <span style={actionIconWrapperStyle}>
                    <StopOutlined style={antActionIconStyle} />
                  </span>
                </Popconfirm>
              </Space>
            ) : record?.status === "Withheld" ? (
              renderActionIcon(
                RedoOutlined,
                () => actionMedicine(record?.id, { status: "Started" }),
                "Resume"
              )
            ) : (
              record?.status === "Stopped" && (
                <UiButton
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowAddMedicine(true);
                    setEditData(record);
                  }}
                >
                  Add Again
                </UiButton>
              )
            )}
            {record?.status === "Stopped" ? (
              <Tag color="red" style={{ margin: 0 }}>
                Stopped
              </Tag>
            ) : (
              record?.status === "Withheld" && (
                <Tag color="yellow" style={{ margin: 0 }}>
                  Withheld
                </Tag>
              )
            )}
          </div>
        );
      },
    });
  }

  return (
    <>
      <Form
        form={form}
        onFinish={onFinish}
        requiredMark={false}
        layout="vertical"
      >
        {showAddMedicine && (
          <AddMedicineForm
            medicineData={data}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            form={form}
            selectedFrequency={selectedFrequency}
            setSelectedFrequency={setSelectedFrequency}
            initial={initial}
            editData={editData}
            refreshPage={refreshPage}
            setShowAddMedicine={setShowAddMedicine}
          />
        )}
        <div
          className={`${styles.searchBar} ${
            showAddMedicine ? styles.searchBarWithFormOpen : ""
          }`}
        >
          <UiTablePageToolbar
            searchAlign="start"
            placeholder="Search medicine"
            searchValue={nameSearch}
            onSearch={(e) => setNameSearch(e?.target?.value ?? "")}
            trailing={
              !showAddMedicine ? (
                <div className={styles.searchBarActions}>
                {actionsEnabled && isActive && (
                  <>
                    <UiButton
                      variant="ghost"
                      size="md"
                      style={{
                        color: token.colorPrimary,
                        borderColor: token.colorPrimary,
                      }}
                      onClick={() => {
                        setShowAddMedicine(true);
                        form.resetFields();
                        setEditData(null);
                      }}
                    >
                      Add Medicine
                    </UiButton>
                    <UiButton
                      variant="primary"
                      icon={<AudioOutlined />}
                      size="md"
                      onClick={() => {
                        setOpenModal(true);
                        setStartRecord(true);
                      }}
                    >
                      AI Prescription
                    </UiButton>
                  </>
                )}
                <UiButton
                  variant="ghost"
                  size="md"
                  style={{
                    color: token.colorPrimary,
                    borderColor: token.colorPrimary,
                  }}
                  onClick={() => {
                    if (typeof setHistoryModal === "function") {
                      setHistoryModal(true);
                    }
                  }}
                >
                  Medication History
                </UiButton>
              </div>
              ) : null
            }
          />
        </div>
        <div className={styles.tableWrap}>
          <Table
            loading={loading}
            dataSource={filteredData}
            columns={columns}
            pagination={false}
            bordered
            rowKey="id"
            scroll={{ x: isMobile ? 720 : 1200 }}
          />
        </div>
      </Form>
      {/* <VoiceRecorder /> */}
      {addModal && (
        <AddMedicineModal
          medicineData={data}
          open={addModal}
          setOpen={setAddModal}
          refreshPage={refreshPage}
          editData={editData}
          initial={editData?.is_initial}
        />
      )}
      {initialModal && (
        <AddInitialMedicineModal
          open={initialModal}
          setOpen={setInitialModal}
          refreshPage={refreshPage}
          editData={editData}
        />
      )}
      {openModal && (
        <RecordingModal
          open={openModal}
          setOpen={setOpenModal}
          medicineData={data}
          startRecord={startRecord}
          setStartRecord={setStartRecord}
          refreshPage={refreshPage}
          setShowAddMedicine={setShowAddMedicine}
        />
      )}
      <Modal
        title={editCanExtendDuration ? "Edit duration & notes" : "Edit notes"}
        open={!!editDurationRecord}
        onCancel={closeEditDurationModal}
        footer={null}
        destroyOnClose
      >
        <Form form={editDurationForm} layout="vertical" requiredMark={false}>
          {editCanExtendDuration && (
            <Form.Item
              label={formatMedicineDurationModalLabel(
                editDaysProgress.noOfDays,
                editDaysProgress.duration
              )}
              name="extended_duration"
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }
                    const n = Number(value);
                    if (!Number.isFinite(n) || n < 0) {
                      return Promise.reject("Enter a valid number of days");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                min={0}
                placeholder="Additional days"
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}
          <Form.Item label="Notes" name="comments">
            <Input.TextArea rows={3} placeholder="Enter notes" />
          </Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={closeEditDurationModal}>Close</Button>
            <Button
              type="primary"
              loading={editDurationSaving}
              onClick={submitEditDuration}
            >
              Submit
            </Button>
          </Space>
        </Form>
      </Modal>
    </>
  );
};

export default AddMedicineTable;
